import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/utils';

export interface TransitionOption {
  /** Backend action: a real table action, or ADMIN_FORCE_TRANSITION with targetState. */
  action: string;
  /** For ADMIN_FORCE_TRANSITION, the state to jump to (sent in metadata). */
  targetState?: string;
  label: string;
  description: string;
  dangerous?: boolean;
}

// Options map to the backend transition table: dispute resolutions use
// their real actions; everything else is an audited admin override.
const transitions: Record<string, TransitionOption[]> = {
  PAYMENT_AUTHORIZED: [
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'CANCELLED', label: 'Cancel Order', description: 'Cancel and void the payment hold', dangerous: true },
  ],
  PENDING_BUSINESS_APPROVAL: [
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'CANCELLED', label: 'Cancel Order', description: 'Cancel on behalf of shopper (voids hold, releases stock)', dangerous: true },
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'BUSINESS_APPROVED', label: 'Force Approve', description: 'Approve on behalf of the business' },
  ],
  BUSINESS_APPROVED: [
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'CANCELLED', label: 'Cancel Order', description: 'Cancel before dispatch', dangerous: true },
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'DISPATCHING_DRIVER', label: 'Force Dispatch', description: 'Manually trigger courier dispatch' },
  ],
  DISPATCHING_DRIVER: [
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'CANCELLED', label: 'Cancel Order', description: 'No couriers available — cancel and refund hold', dangerous: true },
  ],
  DRIVER_ASSIGNED: [
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'DISPATCHING_DRIVER', label: 'Re-dispatch', description: 'Unassign driver and re-run dispatch' },
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'CANCELLED', label: 'Cancel Order', description: 'Cancel pre-pickup', dangerous: true },
  ],
  PICKED_UP: [
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'OUT_FOR_DELIVERY', label: 'Mark Out for Delivery', description: 'Courier is en route' },
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'DELIVERED', label: 'Force Complete', description: 'Mark as delivered' },
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'DISPUTED', label: 'Mark Failed', description: 'Delivery failed — open a dispute', dangerous: true },
  ],
  OUT_FOR_DELIVERY: [
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'DELIVERED', label: 'Force Complete', description: 'Mark as delivered' },
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'DISPUTED', label: 'Mark Failed', description: 'Delivery failed — open a dispute', dangerous: true },
  ],
  DELIVERED: [
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'COMPLETED', label: 'Mark Complete', description: 'Close the order now (skips 24h hold)' },
    { action: 'ADMIN_FORCE_TRANSITION', targetState: 'DISPUTED', label: 'Open Dispute', description: 'Flag this delivery for review', dangerous: true },
  ],
  DISPUTED: [
    { action: 'RESOLVE_DISPUTE_REFUND', label: 'Resolve for Shopper', description: 'Full refund via Stripe', dangerous: true },
    { action: 'RESOLVE_DISPUTE_DENY', label: 'Resolve for Business', description: 'Deny the dispute — order completes' },
  ],
};

interface ForceTransitionPanelProps {
  orderId: string;
  currentState: string;
  onTransition: (option: TransitionOption, reason: string) => Promise<void>;
}

export function ForceTransitionPanel({
  orderId,
  currentState,
  onTransition,
}: ForceTransitionPanelProps) {
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const availableTransitions = transitions[currentState] || [];
  const optionKey = (t: TransitionOption) => `${t.action}:${t.targetState || ''}`;
  const selectedTransition = availableTransitions.find(t => optionKey(t) === selectedAction);

  const handleSubmit = async () => {
    if (!selectedTransition || !reason.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await onTransition(selectedTransition, reason);
      setSelectedAction(null);
      setReason('');
      setIsExpanded(false);
    } catch (err: any) {
      setError(err.message || 'Failed to execute transition');
    } finally {
      setLoading(false);
    }
  };

  if (availableTransitions.length === 0) {
    return (
      <Card className="border-gray-200">
        <CardContent className="p-4 text-center">
          <Shield className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-body">No admin actions available for this state</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200 bg-amber-50/50">
      <CardHeader className="pb-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            <CardTitle className="text-base">Admin Override</CardTitle>
          </div>
          <ChevronDown className={cn(
            "h-5 w-5 text-gray-400 transition-transform",
            isExpanded && "rotate-180"
          )} />
        </button>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="pt-2">
          <p className="text-xs text-body mb-4">
            These actions bypass normal flow. All actions are logged and auditable.
          </p>

          {/* Action selection */}
          <div className="space-y-2 mb-4">
            {availableTransitions.map((transition) => (
              <button
                key={optionKey(transition)}
                onClick={() => setSelectedAction(optionKey(transition))}
                className={cn(
                  "w-full text-left p-3 rounded-lg border-2 transition-all",
                  selectedAction === optionKey(transition)
                    ? transition.dangerous
                      ? "border-error bg-error/5"
                      : "border-primary bg-primary/5"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                )}
              >
                <div className="flex items-center justify-between">
                  <span className={cn(
                    "font-medium text-sm",
                    transition.dangerous ? "text-error" : "text-heading"
                  )}>
                    {transition.label}
                  </span>
                  {transition.dangerous && (
                    <span className="text-xs bg-error/10 text-error px-2 py-0.5 rounded-full">
                      Destructive
                    </span>
                  )}
                </div>
                <p className="text-xs text-body mt-1">{transition.description}</p>
              </button>
            ))}
          </div>

          {/* Reason input */}
          {selectedAction && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-heading mb-1">
                  Reason (required)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why this action is being taken..."
                  rows={3}
                  className="w-full rounded-lg border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light resize-none"
                />
              </div>

              {error && (
                <div className="bg-error/10 text-error text-sm p-3 rounded-lg">
                  {error}
                </div>
              )}

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedAction(null);
                    setReason('');
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={!reason.trim()}
                  className={cn(
                    selectedTransition?.dangerous && "bg-error hover:bg-error/90"
                  )}
                >
                  Execute {selectedTransition?.label}
                </Button>
              </div>
            </div>
          )}

          {/* Audit note */}
          <p className="text-[10px] text-body mt-4 flex items-center">
            <Shield className="h-3 w-3 mr-1" />
            This action will be logged with your admin ID and timestamp
          </p>
        </CardContent>
      )}
    </Card>
  );
}
